import { config } from '../util/config'
import { logger } from '../util/logger'
import posthog from 'posthog-js'

export type PosthogProperties = Record<string, unknown>

let initialised = false

/**
 * The only file that talks to the PostHog SDK. Everything above it goes through
 * `analyticsService`, which owns event names.
 *
 * Initialised once at app boot, for guests and members alike — a visitor on the sign-in page
 * gets an anonymous distinct id, and `identify` on sign-in merges that timeline into the
 * account, which is the pre-auth funnel Mixpanel's identity model could not give us.
 *
 * A build without `VITE_POSTHOG_API_KEY` never initialises, and every method is then a no-op.
 */
export const posthogService = {
  init: (): void => {
    if (initialised) return
    const { apiKey, host } = config.posthog
    if (!apiKey) {
      logger.warn('PostHog: VITE_POSTHOG_API_KEY is not set, product analytics disabled for this build')
      return
    }
    try {
      posthog.init(apiKey, {
        api_host: host,
        // Route changes are reported from the app shell (`app/index.tsx`); the SDK's own
        // pageview would double-count the first load.
        capture_pageview: false,
        capture_pageleave: true,
        // Click autocapture is noise for a video app; every event here is named on purpose.
        autocapture: false,
        respect_dnt: true,
        persistence: 'localStorage+cookie',
      })
      initialised = true
    } catch (err) {
      logger.warn('PostHog: init failed', err)
    }
  },

  isEnabled: (): boolean => initialised,

  capture: (event: string, properties?: PosthogProperties): void => {
    if (!initialised) return
    void posthog.capture(event, properties)
  },

  pageview: (properties?: PosthogProperties): void => {
    if (!initialised) return
    void posthog.capture('$pageview', properties)
  },

  identify: (distinctId: string, params: { set?: PosthogProperties; setOnce?: PosthogProperties }): void => {
    if (!initialised) return
    posthog.identify(distinctId, params.set, params.setOnce)
  },

  /** Person properties without re-identifying. Returns whether the write was attempted. */
  setPersonProperties: (params: { set?: PosthogProperties; setOnce?: PosthogProperties }): boolean => {
    if (!initialised) return false
    posthog.setPersonProperties(params.set, params.setOnce)
    return true
  },

  /** Super properties: attached to every subsequent event until `reset()`. */
  register: (properties: PosthogProperties): void => {
    if (!initialised) return
    posthog.register(properties)
  },

  /** New anonymous distinct id. Must run on sign-out or the next account inherits this one. */
  reset: (): void => {
    if (!initialised) return
    posthog.reset()
  },
}
